import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type NoteContentPastedEventProps } from '@/models/analytics/features/notes';
import { logger } from '@/utils/logger/logger';
import { type PasteFlavorDataFailureReporter } from '@heidi/editor/core';
import { getCurrentSessionId } from '@heidi/runtime/shell';

/**
 * Logger adapter for the classifier's one unreadable input, shared by both
 * editors so the trace is identical wherever the paste landed. Without it the
 * fallback is invisible: an unreadable VS Code payload is classified
 * `vscode_code`, indistinguishable from a genuine non-markdown copy, so it
 * silently inflates that bucket in `note_content_pasted`.
 */
export const reportPasteFlavorDataFailure =
  (
    editorType: NoteContentPastedEventProps['editor_type']
  ): PasteFlavorDataFailureReporter =>
  ({ errorName, dataLength }) => {
    logger.warn('Note paste flavor data unreadable; classified as vscode_code', {
      session_id: getCurrentSessionId(),
      editor_type: editorType,
      errorName,
      dataLength,
    });
  };

/**
 * Fire-and-forget paste telemetry for the note editors: one event per paste,
 * classified by clipboard flavor and stamped with the editor that received it.
 *
 * - Snowplow-first (`amplitude: false`) — a new event, per the analytics policy.
 * - PHI-free: flavor and length only, never pasted content.
 * - Never throws: instrumentation must not break a paste.
 */
export const emitNoteContentPasted = ({
  editorType,
  pasteFlavor,
  contentLength,
}: {
  editorType: NoteContentPastedEventProps['editor_type'];
  pasteFlavor: NoteContentPastedEventProps['paste_flavor'];
  contentLength: number;
}): void => {
  try {
    track({
      event_name: TrackingEventName.NoteContentPasted,
      props: {
        session_id: getCurrentSessionId(),
        editor_type: editorType,
        paste_flavor: pasteFlavor,
        content_length: contentLength,
      },
      options: { amplitude: false },
    });
  } catch (emitError) {
    logger.warn('Note paste analytics emit failed', emitError);
  }
};
